/**
 * usePermissions Hook
 * 
 * Provides permission checking utilities for role-based access control.
 * Works with both agency owners and employees.
 */

import { useCallback, useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import type { EmployeePermissions, EmployeeRole, PagePermissionModule } from '../types/employee';
import { normalizePermissions } from '../utils/employeePermissions';
import accessPolicy from '../config/employeeAccessPolicy.json';

// User type distinguishing agency owner from employee
export type UserType = 'agency' | 'employee';

// Current user info for permission checking
export interface CurrentUserInfo {
  userType: UserType;
  role?: EmployeeRole;
  permissions?: EmployeePermissions;
  isOwner: boolean;
  agencyId?: string;
}

// Permission path format: 'category.action' e.g. 'vehicles.create'
export type PermissionPath =
  | `${PagePermissionModule}.${string}`
  | `vehicles.${keyof EmployeePermissions['vehicles']}`
  | `bookings.${keyof EmployeePermissions['bookings']}`
  | `customers.${keyof EmployeePermissions['customers']}`
  | `finances.${keyof EmployeePermissions['finances']}`
  | `analytics.${keyof EmployeePermissions['analytics']}`
  | `employees.${keyof EmployeePermissions['employees']}`
  | `settings.${keyof EmployeePermissions['settings']}`
  | `pos.${keyof EmployeePermissions['pos']}`
  | `roles.${keyof EmployeePermissions['roles']}`
  | `tasks.${keyof EmployeePermissions['tasks']}`;

// Default permissions for agency owner (full access)
const OWNER_PERMISSIONS: EmployeePermissions = {
  vehicles: { view: true, create: true, edit: true, delete: true },
  bookings: { view: true, create: true, edit: true, cancel: true, approve: true },
  customers: { view: true, create: true, edit: true },
  finances: { view: true, transactions: true, reports: true },
  analytics: { view: true, export: true },
  employees: { view: true, create: true, edit: true, delete: true },
  settings: { view: true, edit: true },
  pos: { access: true, handover: true, return: true, payments: true },
  roles: { view: true, create: true, edit: true, delete: true },
  tasks: { view: true, create: true, edit: true, delete: true }
};


/**
 * Parse a permission path into category and action
 */
const parsePermissionPath = (path: PermissionPath): { category: string; action: string } => {
  const [category, action] = path.split('.') as [string, string];
  return { category, action };
};

/**
 * Check if a permission is granted based on permissions object
 */
const checkPermission = (permissions: EmployeePermissions, path: PermissionPath): boolean => {
  const { category, action } = parsePermissionPath(path);

  const categoryPermissions = permissions[category as keyof EmployeePermissions];
  if (!categoryPermissions) return false;

  return (categoryPermissions as Record<string, boolean>)[action] === true;
};

/**
 * usePermissions Hook
 * 
 * Returns permission checking utilities based on current user type
 */
export const usePermissions = () => {
  const { user, employee, userType, isAuthenticated } = useAuthStore();
  const permissionsSuspended = accessPolicy.enforceEmployeePermissions === false
    && isAuthenticated && userType === 'employee' && Boolean(employee?.agency?.id);

  // Get current user info (agency owner or employee)
  const currentUser = useMemo((): CurrentUserInfo => {
    // Check if there's an employee session
    if (isAuthenticated && employee && userType === 'employee') {
      return {
        userType: 'employee',
        role: employee.role,
        permissions: employee.permissions,
        isOwner: false,
        agencyId: employee.agency?.id
      };
    }

    // Fall back to agency owner
    if (user && isAuthenticated && userType === 'agency') {
      return {
        userType: 'agency',
        role: 'owner',
        permissions: OWNER_PERMISSIONS,
        isOwner: true,
        agencyId: user._id
      };
    }

    // Not authenticated
    return {
      userType: 'agency',
      isOwner: false
    };
  }, [user, employee, userType, isAuthenticated]);

  // Get effective permissions (owner gets full, employees get their permissions)
  const permissions = useMemo((): EmployeePermissions => {
    if (currentUser.isOwner) {
      return OWNER_PERMISSIONS;
    }
    return normalizePermissions(currentUser.permissions);
  }, [currentUser]);

  /**
   * Check if user has a specific permission
   * @param path Permission path like 'vehicles.create' or 'bookings.approve'
   * @returns true if user has the permission
   */
  const hasPermission = useCallback((path: PermissionPath): boolean => {
    // Owners always have all permissions
    if (currentUser.isOwner) return true;
    if (accessPolicy.enforceBookingPermissions) {
      if (path.startsWith('bookings.')) return checkPermission(permissions, path);
      if (['pos.handover', 'pos.return', 'pos.payments'].includes(path)) return checkPermission(permissions, 'bookings.edit');
    }
    if (permissionsSuspended) return true;

    return checkPermission(permissions, path);
  }, [currentUser.isOwner, permissions, permissionsSuspended]);

  /**
   * Check if user has any of the specified permissions
   * @param paths Array of permission paths
   * @returns true if user has at least one of the permissions
   */
  const hasAnyPermission = useCallback((paths: PermissionPath[]): boolean => {
    return paths.some(path => hasPermission(path));
  }, [hasPermission]);

  /**
   * Check if user has all of the specified permissions
   * @param paths Array of permission paths
   * @returns true if user has all of the permissions
   */
  const hasAllPermissions = useCallback((paths: PermissionPath[]): boolean => {
    return paths.every(path => hasPermission(path));
  }, [hasPermission]);

  /**
   * Check if user has permission for a category (any action in category)
   * @param category Permission category like 'vehicles', 'bookings'
   * @returns true if user has any permission in the category
   */
  const hasCategoryAccess = useCallback((category: keyof EmployeePermissions): boolean => {
    if (category === 'bookings' && accessPolicy.enforceBookingPermissions && !currentUser.isOwner) {
      return Object.values(permissions.bookings || {}).some(value => value === true);
    }
    if (currentUser.isOwner || permissionsSuspended) return true;

    const categoryPermissions = permissions[category];
    if (!categoryPermissions) return false;

    return Object.values(categoryPermissions).some(value => value === true);
  }, [currentUser.isOwner, permissions, permissionsSuspended]);

  /**
   * Check if user has a specific role or higher
   * Role hierarchy: owner > manager > staff/accountant/customer_service > driver
   */
  const hasRole = (role: EmployeeRole): boolean => {
    if (permissionsSuspended) return true;
    if (!currentUser.role) return false;

    const roleHierarchy: Record<EmployeeRole, number> = {
      owner: 100,
      manager: 80,
      accountant: 60,
      receptionist: 55,
      customer_service: 50,
      staff: 40,
      driver: 20
    };

    return roleHierarchy[currentUser.role] >= roleHierarchy[role];
  };

  /**
   * Check if user has exactly a specific role
   */
  const isRole = (role: EmployeeRole): boolean => {
    return currentUser.role === role;
  };

  /**
   * Check if user can manage employees (only owner and manager)
   */
  const canManageEmployees = (): boolean => {
    return hasPermission('employees.view');
  };

  /**
   * Check if user can access settings
   */
  const canAccessSettings = (): boolean => {
    return hasPermission('settings.view');
  };

  /**
   * Check if user can access finances
   */
  const canAccessFinances = (): boolean => {
    return hasPermission('finances.view');
  };

  /**
   * Check if user can access analytics
   */
  const canAccessAnalytics = (): boolean => {
    return hasPermission('analytics.view');
  };

  /**
   * Check if user can access POS
   */
  const canAccessPOS = (): boolean => {
    return hasPermission('pos.access');
  };

  /**
   * Check if user can manage roles
   */
  const canManageRoles = (): boolean => {
    return hasPermission('roles.view');
  };

  /**
   * Check if user can manage tasks
   */
  const canManageTasks = (): boolean => {
    return hasPermission('tasks.view');
  };

  return {
    // User info
    currentUser,
    userType: currentUser.userType,
    role: currentUser.role,
    isOwner: currentUser.isOwner,
    permissions,

    // Permission checkers
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasCategoryAccess,

    // Role checkers
    hasRole,
    isRole,

    // Convenience methods
    canManageEmployees,
    canAccessSettings,
    canAccessFinances,
    canAccessAnalytics,
    canAccessPOS,
    canManageRoles,
    canManageTasks
  };
};

export default usePermissions;
